import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Gift, Users, Share2, Copy, Loader2, Check, UserPlus, Sparkles,
  Scissors, Download,
} from "lucide-react";

export default function Referral() {
  const navigate = useNavigate();
  const { tailleur } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    api.get("/referral").then((r) => setData(r.data)).catch(() => toast.error("Erreur de chargement")).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-tp-bg flex items-center justify-center"><Loader2 className="animate-spin text-tp-green" /></div>;
  }

  const origin = window.location.origin;
  const link = `${origin}/bienvenue?ref=${data.code}`;
  const message = `🧵 Salut ! J'utilise *Tailleur Pro* pour gérer les mesures de mes clients (même sans internet). Télécharge l'appli avec mon lien et on gagne tous les deux des clients gratuits :\n${link}`;

  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  const shareNative = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Tailleur Pro", text: message, url: link }); } catch { /* cancelled */ }
    } else {
      shareWhatsApp();
    }
  };
  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copié`); }
    catch { toast.error("Copie impossible"); }
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#0E5439" });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `tailleur-pro-code-${data.code}.png`;
      a.click();
      toast.success("Carte enregistrée");
    } catch {
      toast.error("Impossible de générer l'image");
    } finally {
      setDownloading(false);
    }
  };

  const filleuls = data.filleuls;
  const unlocked = filleuls >= data.unlock_at;
  const progressPct = unlocked ? 100 : Math.round((filleuls / data.unlock_at) * 100);

  return (
    <div className="min-h-screen bg-tp-bg pb-10">
      <div className="bg-tp-green text-white px-4 pt-5 pb-6 flex items-center gap-3">
        <button data-testid="referral-back-btn" onClick={() => navigate("/")} className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center"><ArrowLeft size={20} /></button>
        <h1 className="font-head font-bold text-xl">Parrainage</h1>
      </div>

      <div className="px-5 -mt-3">
        <div className="bg-tp-gold rounded-2xl p-5 text-tp-ink text-center">
          <Gift size={40} className="mx-auto mb-2" />
          <p className="font-head text-xl font-extrabold">Gagnez des clients gratuits</p>
          <p className="text-sm mt-1 font-medium">
            Invitez 2 tailleurs qui téléchargent l'appli → <b>+10 clients</b>. Puis <b>+5 clients</b> à chaque nouvel invité !
          </p>
          <p className="text-xs mt-2 font-semibold bg-tp-ink/10 rounded-lg px-2 py-1 inline-block">
            🎁 Chaque filleul reçoit aussi +{data.filleul_reward ?? 5} clients offerts
          </p>
        </div>
      </div>

      {data.welcome_bonus > 0 && (
        <div className="px-5 mt-3">
          <div data-testid="welcome-bonus" className="flex items-center gap-2 bg-tp-green/10 border border-tp-green/30 rounded-xl px-4 py-3">
            <Sparkles size={18} className="text-tp-green shrink-0" />
            <p className="text-sm text-tp-ink font-semibold">Vous avez reçu <b>+{data.welcome_bonus} clients offerts</b> grâce à votre parrain 🎉</p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="px-5 mt-4 grid grid-cols-2 gap-3">
        <div data-testid="referral-filleuls" className="bg-white rounded-xl p-4 border border-gray-100">
          <Users size={20} className="text-tp-green mb-1.5" />
          <p className="font-head text-3xl font-extrabold text-tp-ink">{filleuls}</p>
          <p className="text-sm text-tp-muted">Tailleurs invités</p>
        </div>
        <div data-testid="referral-bonus" className="bg-white rounded-xl p-4 border border-gray-100">
          <Sparkles size={20} className="text-tp-gold mb-1.5" />
          <p className="font-head text-3xl font-extrabold text-tp-ink">+{data.bonus_clients}</p>
          <p className="text-sm text-tp-muted">Clients bonus</p>
        </div>
      </div>

      {/* Progress */}
      <div className="px-5 mt-3">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-tp-ink">
              {unlocked ? `Prochain palier : +${data.next_reward} clients` : `Encore ${data.needed_for_next} invité(s) pour +${data.next_reward} clients`}
            </p>
            {unlocked && <Check size={18} className="text-tp-green" />}
          </div>
          <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full bg-tp-green rounded-full transition-all" style={{ width: `${progressPct}%` }} />
          </div>
          <p className="text-xs text-tp-muted mt-2">Limite actuelle : <b>{data.client_limit} clients</b> (base {data.base_limit})</p>
        </div>
      </div>

      {/* Shareable card */}
      <div className="px-5 mt-5">
        <p className="text-xs text-tp-muted uppercase tracking-wider font-semibold mb-2">Ma carte à partager</p>
        <div
          ref={cardRef}
          data-testid="referral-card"
          className="relative rounded-2xl overflow-hidden p-6 text-white"
          style={{
            backgroundColor: "#0E5439",
            backgroundImage:
              "radial-gradient(circle at 15% 20%, rgba(229,169,36,0.20) 0 12px, transparent 13px), radial-gradient(circle at 85% 12%, rgba(229,169,36,0.14) 0 20px, transparent 21px), radial-gradient(circle at 78% 88%, rgba(229,169,36,0.16) 0 16px, transparent 17px), radial-gradient(circle at 8% 90%, rgba(255,255,255,0.06) 0 24px, transparent 25px)",
          }}
        >
          <div className="flex items-center gap-2 mb-6">
            <div className="w-9 h-9 rounded-xl bg-tp-gold flex items-center justify-center">
              <Scissors size={18} className="text-tp-ink" />
            </div>
            <span className="font-head text-lg font-extrabold">Tailleur Pro</span>
          </div>

          <p className="text-white/75 text-sm">
            {tailleur?.atelier || tailleur?.nom || "Mon atelier"} vous invite
          </p>
          <p className="font-head text-2xl font-extrabold leading-tight mt-1">
            Rejoignez Tailleur&nbsp;Pro 🧵
          </p>

          <div className="my-6 rounded-xl border-2 border-dashed border-tp-gold/70 bg-black/10 py-4 text-center">
            <p className="text-tp-gold/90 text-xs uppercase tracking-widest font-semibold mb-1">Mon code de parrainage</p>
            <p className="font-head text-5xl font-extrabold tracking-[0.3em] pl-[0.3em]">{data.code}</p>
          </div>

          <p className="text-white/85 text-sm leading-relaxed">
            Téléchargez l'appli, entrez ce code à l'inscription, et gérez toutes vos mesures clients — même sans internet.
          </p>
          <p className="text-tp-gold text-xs mt-3 break-all">{link}</p>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3">
          <Button data-testid="download-card-btn" onClick={downloadCard} disabled={downloading} className="h-12 rounded-full bg-tp-green hover:bg-tp-greenDark font-bold">
            {downloading ? <Loader2 className="animate-spin" size={18} /> : <><Download size={18} /> Enregistrer</>}
          </Button>
          <Button data-testid="share-card-whatsapp-btn" onClick={shareWhatsApp} className="h-12 rounded-full bg-tp-whatsapp hover:brightness-105 font-bold">
            <Share2 size={18} /> Partager
          </Button>
        </div>
      </div>

      {/* Code + share */}
      <div className="px-5 mt-5">
        <p className="text-sm font-semibold text-tp-ink mb-1.5">Votre code de parrainage</p>
        <div className="flex gap-2">
          <div className="flex-1 h-14 rounded-xl bg-white border-2 border-dashed border-tp-green flex items-center justify-center">
            <span data-testid="referral-code" className="font-head text-2xl font-extrabold tracking-widest text-tp-green">{data.code}</span>
          </div>
          <button data-testid="copy-code-btn" onClick={() => copy(data.code, "Code")} className="w-14 h-14 rounded-xl bg-white border border-gray-300 flex items-center justify-center text-tp-green">
            <Copy size={20} />
          </button>
        </div>

        <Button data-testid="share-whatsapp-btn" onClick={shareWhatsApp} className="w-full h-[54px] rounded-full bg-tp-whatsapp hover:brightness-105 font-bold text-base mt-4">
          <Share2 size={20} /> Inviter sur WhatsApp
        </Button>
        <Button data-testid="share-link-btn" onClick={shareNative} variant="outline" className="w-full h-12 rounded-full border-tp-green text-tp-green font-semibold mt-2">
          <UserPlus size={18} /> Partager le lien
        </Button>
      </div>

      {/* Filleuls list */}
      {data.noms?.length > 0 && (
        <div className="px-5 mt-6">
          <p className="text-xs text-tp-muted uppercase tracking-wider font-semibold mb-2">Vos filleuls</p>
          <div className="space-y-2">
            {data.noms.map((f, i) => (
              <div key={i} className="bg-white rounded-xl p-3.5 border border-gray-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-tp-green/10 flex items-center justify-center"><Check size={16} className="text-tp-green" /></div>
                <div className="min-w-0">
                  <p className="font-semibold text-tp-ink truncate">{f.nom}{f.atelier ? ` · ${f.atelier}` : ""}</p>
                  <p className="text-sm text-tp-muted">A rejoint Tailleur Pro</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
